
import React from "react";
import { Schemes, Utils } from "../../../utils";
import { useSearchParams } from "react-router-dom";
import { FormControls, i18nManager } from "../../../i18n";
import { ConfirmDialog, Elements, Siren } from "../../shared";
import { BaseService, ConsusService, IConsusFile, SanitizedResponse } from "../../../services";
import { ButtonManageRef, Column, FileHelper, FileInput, FileInputManageRef, FormControl, FormGroup, InputManageRef, NoseurObject, Scheme, Table, TabPane, TabPanel, TextAreaInput } from "@rivtn/noseur";

function ManageFiles() {
    const consusService = ConsusService.getInstance();
    const dialogErrorRef = React.useRef<HTMLDivElement>(null);
    const dialogTextInputRef = React.useRef<InputManageRef>(null);
    const dialogNameInputRef = React.useRef<InputManageRef>(null);
    const dialogMimeTypeInputRef = React.useRef<InputManageRef>(null);
    const dialogFileInputRef = React.useRef<FileInputManageRef>(null);
    const labels = i18nManager.Labels.dashboard.administration.manage_files;
    const [activeConsusFile, setActiveConsusFile] = React.useState<IConsusFile>();
    const [searchParams, setSearchParams] = useSearchParams(window.location.search);
    const [consusFiles, setConsusFiles] = React.useState<IConsusFile[] | undefined>();

    React.useEffect(fetchConsusFiles, [searchParams]);

    return (<div className="platform-page">
        <div className="management">
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>{labels.title}</span>
                <div style={{ display: "flex", fontSize: 15 }}>
                    <span className="link" onClick={() => uploadData("secured")}>{labels.upload_secure_file}</span>
                    <span className="link" style={{ marginLeft: 15 }} onClick={() => uploadData("raw_data")}>{labels.upload_raw_data}</span>
                    <span className="link" style={{ marginLeft: 15 }} onClick={() => uploadData("form_data")}>{labels.upload_with_form_data}</span>
                    <span className="link" style={{ marginLeft: 15 }} onClick={() => uploadData("binary")}>{labels.upload_binary}</span>
                </div>
            </div>
            <div className="query-inputs">
                <FormGroup scheme={Schemes.RIVTN_QUIRINUS} className="form" style={{ justifyContent: "flex-start" }}>
                    {labels.search_fields.map(({ disabled, icon, type, name, placeholder, options }: FormControls, index: number) => {
                        const inputProps: any = {
                            name,
                            fill: true,
                            placeholder,
                            noStyle: true,
                            borderless: true,
                            style: { borderRadius: 0 },
                            disabled: (disabled === true),
                            defaultValue: searchParams.get(name),
                            onInputComplete: (e: string) => Utils.updateSearchParams(name, e ?? undefined, setSearchParams),
                        };
                        if (type === "dropdown") {
                            inputProps.options = options;
                            inputProps.onInputComplete = undefined;
                            inputProps.onSelectOption = (option: any) => {
                                Utils.updateSearchParams(name, option.value ?? undefined, setSearchParams);
                                return false;
                            };
                            inputProps.selectedOptionIndex = options?.findIndex(c => c.value === (searchParams.get(name) ?? "user")) ?? 0;
                        }
                        return (<FormControl key={index} leftContent={icon} validStyle={{ borderColor: "transparent" }}
                            contentStyle={{ borderRadius: 0 }} style={type == "text" ? { flex: 1, maxWidth: "none" } : { marginRight: 15 }}>
                            {React.createElement(Utils.selectNoseurComponent(type!) as any, inputProps)}
                        </FormControl>);
                    })}
                </FormGroup>
            </div>
            <Table className="table" rowProps={{ className: "audit" }} data={consusFiles} rowsPerPage={consusFiles?.length ?? 10} totalRecords={consusFiles?.length}
                loadingState={Elements.LOADING_STATE} onRowSelection={(consusFile: IConsusFile) => {
                    setActiveConsusFile(consusFile);
                    return true;
                }} emptyState={(<div style={{ margin: "auto", display: "flex", flexDirection: "column", alignItems: "center", marginTop: "10%" }}>
                    <i className="fa fa-bullseye" style={{ marginBottom: 20, fontSize: 40, color: "#0FA883" }} />
                    <span style={{ maxWidth: 230, textAlign: "center" }}>
                        {labels.empty_labels_parts.no_uploaded_data}{" "}
                        <span className="link" onClick={() => uploadData("binary")}>{labels.empty_labels_parts.upload}</span>{" "}
                        {labels.empty_labels_parts.a_data_or_update}
                    </span>
                </div>)}>
                <Column sortable header={labels.table.file_name} dataKey="file_name" />
                <Column sortable header={labels.table.replaced_existing} dataKey="replaced_existing" template={(b: "true" | "false") => labels.table.replaced_values[b]} />
                <Column sortable header={labels.table.relative_path} dataKey="relative_path" />
                <Column sortable header={labels.table.url} dataKey="consus_download_url" />
            </Table>
        </div>
        {activeConsusFile ? (<div className="active-data">
            <div className="header">
                <i className="fa fa-times" onClick={() => setActiveConsusFile(undefined)} />
                <span className="name">{activeConsusFile.file_name}</span>
                <span className="id">{activeConsusFile.relative_path.substring(0, activeConsusFile.relative_path.indexOf("/"))}</span>
                <span className="link desc">{activeConsusFile.consus_download_url}</span>
            </div>
            <div className="middle" style={{ borderBottom: "none" }}>
                <a href={activeConsusFile.consus_download_url} target="_blank">{labels.preview.open_file}</a>
                <span className="link danger" style={{ marginTop: 10 }} onClick={deleteFile}>{labels.preview.delete_file}</span>
            </div>
        </div>) : null}
    </div>);

    function fetchConsusFiles() {
        setConsusFiles(undefined);
        consusService.queryConsusFiles(`${searchParams.get("namespace") ?? "user"}/${searchParams.get("folder") ?? ""}`).then(({ apiResponse }: SanitizedResponse<IConsusFile>) => {
            setConsusFiles((apiResponse?.data as any as IConsusFile[]) ?? []);
        }).catch(BaseService.reportError);
    }

    function uploadData(type: "raw_data" | "binary" | "form_data" | "secured") {
        let payload: NoseurObject<any> = {};
        const dialogLabels = labels.dialog[type];

        const dialog = ConfirmDialog({
            closable: true,
            style: { minWidth: 500 },
            title: dialogLabels.title,
            confirmLabel: labels.upload,
            content: (<div style={{ display: "flex", flexDirection: "column" }}>
                {Elements.buildConfirmDialogForm(dialogLabels.form, (name: string, value: string) => {
                    dialogErrorRef.current!.innerText = " ";
                    payload[name] = value;
                    return false;
                }, undefined, ((name, ref) => {
                    if (name === "name-managed") dialogNameInputRef.current = ref;
                    else if (name === "mime_type-managed") dialogMimeTypeInputRef.current = ref;
                }), { marginTop: 0 })}
                {(type === "raw_data") && (<TabPane idleScheme={Scheme.DARK} scheme={Schemes.RIVTN_QUIRINUS} style={{ marginTop: 15 }} onTabActive={(index) => {
                    if (index == 0 && payload.__selected_files && dialogFileInputRef.current) {
                        dialogFileInputRef.current.changeFiles(payload.__selected_files);
                    } else if (index == 1 && payload.content && dialogTextInputRef.current) {
                        dialogTextInputRef.current.setValue(payload.content);
                    }
                }}>
                    <TabPanel header={(dialogLabels as any).preview}>
                        <FileInput scheme={Schemes.RIVTN_QUIRINUS} className="media-drag-component" accepts="*/*"
                            manageRef={dialogFileInputRef} onSelectFiles={async (files) => {
                                payload.__selected_files = files;
                                if (payload.encoding === "TEXT") {
                                    payload.content = await FileHelper.fileToText(payload.__selected_files[0]);
                                } else if (payload.encoding === "BASE64") {
                                    payload.content = await FileHelper.fileToBase64(payload.__selected_files[0]);
                                } else if (payload.encoding === "INT_ARRAY") {
                                    throw new Error("not implemented");
                                } else {
                                    dialogFileInputRef.current?.clear();
                                    dialogErrorRef.current!.innerText = (dialogLabels as any).select_encoding;
                                    return;
                                }
                                const name = (payload.__selected_files[0] as File).name;
                                const type = (payload.__selected_files[0] as File).type;
                                payload.name = name;
                                payload.mime_type = type;
                                dialogNameInputRef.current?.setValue(name);
                                dialogMimeTypeInputRef.current?.setValue(type);
                            }} onRemoveFile={() => {
                                delete payload.content;
                                delete payload.__selected_files;
                            }} />
                    </TabPanel>
                    <TabPanel header={(dialogLabels as any).raw}>
                        <TextAreaInput manageRef={dialogTextInputRef} defaultValue={payload.content} style={{ width: "100%", height: 300, marginTop: 20 }} onInputComplete={(v) => {
                            let type: string;
                            let fileBits: BlobPart[];
                            if (payload.encoding === "TEXT") {
                                fileBits = v.split("");
                                type = dialogMimeTypeInputRef.current?.value() ?? 'text/plain';
                            } else if (payload.encoding === "BASE64") {
                                fileBits = atob(v).split("");
                                type = dialogMimeTypeInputRef.current?.value() ?? 'text/plain';
                            } else if (payload.encoding === "INT_ARRAY") {
                                throw new Error("not implemented");
                            } else {
                                dialogErrorRef.current!.innerText = (dialogLabels as any).select_encoding;
                                return;
                            }
                            payload.content = v;
                            payload.__selected_files = [new File(fileBits, dialogNameInputRef.current?.value() ?? "Unknown", { type })];
                        }} />
                    </TabPanel>
                </TabPane>)}
                {(type !== "raw_data") && (<FileInput scheme={Schemes.RIVTN_QUIRINUS} className="media-drag-component" accepts="*/*"
                    style={{ height: 300 }} manageRef={dialogFileInputRef} onSelectFiles={async (files) => {
                        payload.__selected_files = files;
                        const name = (payload.__selected_files[0] as File).name;
                        payload.name = name;
                        dialogNameInputRef.current?.setValue(name);
                    }} onRemoveFile={() => {
                        delete payload.__selected_files;
                    }} />)}
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            onConfirm: (button) => {
                let method: Function;
                let done: Function | undefined;
                dialogErrorRef.current!.innerText = " ";
                const cachedSelectedFiles = payload.__selected_files;
                delete payload.__selected_files;
                if (type === "raw_data") {
                    if (!payload.folder || !payload.name || !payload.encoding || !payload.mime_type || !payload.content) {
                        dialogErrorRef.current!.innerText = labels.form_fields_required;
                        return false;
                    }
                    method = consusService.uploadRawData.bind(consusService);
                } else if (type === "binary") {
                    done = () => {
                        delete payload.file;
                    };
                    payload.file = cachedSelectedFiles[0];
                    method = consusService.uploadBinary.bind(consusService);
                } else if (type === "form_data") {
                    if (!payload.folder || !payload.name) {
                        dialogErrorRef.current!.innerText = labels.form_fields_required;
                        return false;
                    }
                    done = () => {
                        delete payload.file;
                    };
                    payload.file = cachedSelectedFiles[0];
                    method = consusService.uploadFormData.bind(consusService);
                } else {
                    return false;
                }
                button?.setLoadingState(true);
                method(payload).then(({ apiResponse: { data } }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        uploadSuccessful(data);
                    });
                }).catch((response: any) => {
                    dialogErrorRef.current!.innerText = `${response}`;
                }).finally(() => {
                    done?.();
                    payload.__selected_files = cachedSelectedFiles;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function uploadSuccessful(consusFile: IConsusFile) {
        const dialog = ConfirmDialog({
            onCancel: fetchConsusFiles,
            title: labels.dialog.upload_success.title,
            cancelLabel: i18nManager.Labels.common.cancel,
            confirmLabel: labels.dialog.upload_success.action_label,
            desc: labels.dialog.upload_success.desc.replace("${name}", consusFile.file_name)
                .replace("${url}", consusFile.consus_download_url),
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                Utils.copyToClipboard(consusFile.consus_download_url, () => {
                    fetchConsusFiles();
                    dialog.hide(() => Siren.alert(labels.dialog.upload_success.copy_success));
                });
                return false;
            },
        });
        dialog.show();
    }

    function deleteFile() {
        if (!activeConsusFile) return;
        const dialog = ConfirmDialog({
            confirmScheme: Scheme.DANGER,
            title: labels.dialog.delete.title,
            cancelLabel: i18nManager.Labels.common.cancel,
            desc: labels.dialog.delete.desc.replace("${name}", activeConsusFile.file_name)
                .replace("${url}", activeConsusFile.consus_download_url),
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                consusService.deleteFiles([activeConsusFile.relative_path]).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(fetchConsusFiles);
                    setActiveConsusFile(undefined);
                }).catch(BaseService.reportError).finally(() => {
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

}

export default ManageFiles;
